import { supabase } from '@/integrations/supabase/client';
import {
  MarketplaceProduct,
  MarketplaceProductWithStats,
  CreateProductParams,
  UpdateProductParams,
  ProductFilters,
  ProductPaginationResult
} from '@/types/marketplace/marketplace';

export class ProductService {
  /**
   * Get all products with optional filtering
   */
  static async getProducts(filters?: ProductFilters): Promise<MarketplaceProductWithStats[]> {
    let query = supabase
      .from('marketplace_products')
      .select('*')
      .order('order_index', { ascending: true })
      .order('name', { ascending: true });

    if (filters?.brand_id) {
      console.log('Applying brand filter:', filters.brand_id);
      query = query.eq('brand_id', filters.brand_id);
    }

    if (filters?.category_id) {
      console.log('Applying category filter:', filters.category_id);
      query = query.eq('category_id', filters.category_id);
    }

    if (filters?.has_genies) {
      console.log('Applying has_genies filter');
      query = query.gt('genie_count', 0);
    }

    if (filters?.search) {
      console.log('Applying search filter:', filters.search);
      query = query.or(`name.ilike.%${filters.search}%,model_number.ilike.%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching products:', error);
      throw error;
    }

    console.log('Query returned', data?.length, 'products');
    return data || [];
  }

  /**
   * Get products with pagination and filters
   */
  static async getProductsWithPagination(
    filters?: ProductFilters,
    page: number = 1,
    limit: number = 20
  ): Promise<ProductPaginationResult> {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let query = supabase
      .from('marketplace_products')
      .select(`
        *,
        marketplace_brands:marketplace_brands(id, name, slug, logo_url),
        marketplace_categories:marketplace_categories(id, name, slug)
        `, { count: 'exact' })
      .order('order_index', { ascending: true })
      .order('name', { ascending: true });

    if (filters?.brand_id) {
      query = query.eq('brand_id', filters.brand_id);
    }

    if (filters?.category_id) {
      query = query.eq('category_id', filters.category_id);
    }

    if (filters?.has_genies) {
      query = query.gt('genie_count', 0);
    }

    if (filters?.search) {
      query = query.or(`name.ilike.%${filters.search}%,model_number.ilike.%${filters.search}%`);
    }

    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching products with pagination:', error);
      throw error;
    }

    const total = count ?? 0;
    const totalPages = Math.ceil(total / limit);
    const preparedData = data?.map(product => ({
      ...product,
      brand_name: product?.marketplace_brands?.name,
      brand_slug: product?.marketplace_brands?.slug,
      brand_logo_url: product?.marketplace_brands?.logo_url,
      category_name: product?.marketplace_categories?.name,
      category_slug: product?.marketplace_categories?.slug,
    }));
    return {
      data: preparedData || [],
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasMore: page < totalPages,
      },
    };
  }

  /**
   * Get products by brand slug
   */
  static async getProductsByBrand(brandSlug: string): Promise<MarketplaceProductWithStats[]> {
    const { data: brand } = await supabase
      .from('marketplace_brands')
      .select('id')
      .eq('slug', brandSlug)
      .single();

    if (!brand) return [];

    return this.getProducts({ brand_id: brand.id });
  }

  /**
   * Get products by category slug
   */
  static async getProductsByCategory(categorySlug: string): Promise<MarketplaceProductWithStats[]> {
    const { data: category } = await supabase
      .from('marketplace_categories')
      .select('id')
      .eq('slug', categorySlug)
      .single();

    if (!category) return [];

    return this.getProducts({ category_id: category.id });
  }

  /**
   * Get a single product by brand and product slug
   */
  static async getProductBySlug(brandSlug: string, productSlug: string): Promise<MarketplaceProductWithStats | null> {
    const { data: brand } = await supabase
      .from('marketplace_brands')
      .select('id')
      .eq('slug', brandSlug)
      .single();

    if (!brand) return null;

    const { data, error } = await supabase
      .from('marketplace_products')
      .select('*')
      .eq('brand_id', brand.id)
      .eq('slug', productSlug)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return data;
  }

  /**
   * Get a single product by ID
   */
  static async getProductById(id: string): Promise<MarketplaceProduct | null> {
    const { data, error } = await supabase
      .from('marketplace_products')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data;
  }

  /**
   * Create a new product (admin only)
   */
  static async createProduct(params: CreateProductParams): Promise<MarketplaceProduct> {
    const { data, error } = await supabase
      .from('marketplace_products')
      .insert(params)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Update a product (admin only)
   */
  static async updateProduct(id: string, params: UpdateProductParams): Promise<MarketplaceProduct> {
    const { data, error } = await supabase
      .from('marketplace_products')
      .update(params)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Delete a product (admin only)
   */
  static async deleteProduct(id: string): Promise<void> {
    const { error } = await supabase
      .from('marketplace_products')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  /**
   * Get popular products across all categories
   */
  static async getPopularProducts(limit: number = 12): Promise<MarketplaceProductWithStats[]> {
    const { data, error } = await supabase
      .from('marketplace_products')
      .select('*')
      .gt('genie_count', 0)
      .order('genie_count', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  }
}
